import React, { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { X, MapPin, Anchor, Ship, FileText, Truck, Package, Plane, AlertTriangle } from "lucide-react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PublicIcon from "@mui/icons-material/Public";
import { WL_RED } from "../../utils/designTokens";
import { apiService } from "../../services/api";

const FONT_CARD = "'Inter', sans-serif";

// ── Identical tokens to ShipmentDetailsPopup ──────────────────────────────
const FONT         = "'Inter', sans-serif";
const TEXT_PRIMARY = "#111111";
const TEXT_MUTED   = "#6B7280";
const BORDER_COLOR = "#EAEAEA";
const BG_GREY      = "#F7F7F7";
const GREEN        = "#22C55E";

const STATUS_STYLE: Record<string, { label: string; color: string; bg: string; border: string }> = {
  NOT_STARTED: { label: "pending",     color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE" },
  PENDING:     { label: "pending",     color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE" },
  IN_PROGRESS: { label: "in progress", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE" },
  AT_RISK:     { label: "at risk",     color: "#EA580C", bg: "#FFF7ED", border: "#FED7AA" },
  DELAYED:     { label: "delayed",     color: WL_RED,    bg: "#FEF2F2", border: "#FECACA" },
  COMPLETED:   { label: "completed",   color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" },
};

interface JourneyStep {
  key: string;
  labelFn: (src?: string, dst?: string) => string;
  sub: string;
  Icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
}

const JOURNEY_STEPS: JourneyStep[] = [
  { key: "origin",    labelFn: (src = "—")               => `Origin: ${src}`,             sub: "Shipment picked up",   Icon: MapPin   },
  { key: "departed",  labelFn: ()                         => "Departed Origin Port",       sub: "Left port facility",   Icon: Anchor   },
  { key: "transit",   labelFn: ()                         => "In Transit",                 sub: "Cargo in transit",     Icon: Ship     },
  { key: "customs",   labelFn: (_s?: string, dst = "—")   => `Customs Clearance – ${dst}`, sub: "Awaiting processing",  Icon: FileText },
  { key: "delivery",  labelFn: ()                         => "Out for Delivery",           sub: "Final mile delivery",  Icon: Truck    },
  { key: "delivered", labelFn: ()                         => "Delivered",                  sub: "Shipment complete",    Icon: Package  },
];

const modeIcon = (mode: string) => {
  const m = (mode || "").toUpperCase();
  if (m.includes("AIR"))   return <Plane size={16} style={{ color: "#2563EB" }} />;
  if (m.includes("TRUCK")) return <Truck size={16} style={{ color: "#EA580C" }} />;
  if (m.includes("RAIL"))  return <Truck size={16} style={{ color: "#7C3AED" }} />;
  return <Ship size={16} style={{ color: "#2563EB" }} />;
};

const fmtCurrency = (v: unknown): string => {
  const n = Number(v);
  if (!v || Number.isNaN(n) || n === 0) return "—";
  return "$" + Math.round(n).toLocaleString("en-US");
};

function fmt(v: unknown, fallback = "—"): string {
  if (v === null || v === undefined || String(v).trim() === "") return fallback;
  const s = String(v).trim();
  if (["null", "nan", "none"].includes(s.toLowerCase())) return fallback;
  return s;
}

function fmtMoney(v: unknown): string {
  const n = Number(v);
  if (!v || Number.isNaN(n)) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

/** Convert SNAKE_CASE / ALL_CAPS strings to Title Case for display */
const fmtLabel = (s: string) =>
  s.replaceAll("_", " ").toLowerCase().split(" ").map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");

function priorityColor(label: string | null | undefined): string {
  const l = (label ?? "").toLowerCase();
  if (l.includes("critical") || l.includes("high")) return "#DC2626";
  if (l.includes("medium") || l.includes("mid")) return "#F97316";
  return "#6B7280";
}

interface AtRiskOrder {
  shipment_id?: string | null;
  current_ui_group?: string | null;
  order_value_usd?: number | null;
  region?: string | null;
  carrier_name?: string | null;
  priority_label?: string | null;
  primary_freight_mode?: string | null;
  requires_special_handling?: string | boolean | null;
  is_multimodal?: string | boolean | null;
  risk_flag?: string | null;
  supplier_name?: string | null;
  source_location?: string | null;
  destination_location?: string | null;
  destination?: string | null;
  assigned_route?: string | null;
  route_name?: string | null;
  mode?: string | null;
  transport_mode?: string | null;
  expected_arrival?: string | null;
  estimated_arrival?: string | null;
  eta?: string | null;
  product_name?: string | null;
  product?: string | null;
  order_quantity?: number | null;
  total_weight_kg?: number | null;
  customer_id?: string | null;
  delay_reason?: string | null;
  last_event_ts?: string | null;
  progress_pct?: number | null;
  tariff_impact?: number | null;
  tariff_impact_usd?: number | null;
  tariff_cost?: number | null;
  display_status?: string | null;
  status?: string | null;
  [key: string]: unknown;
}

interface DashboardAlertsProps {
  orders?: AtRiskOrder[];
}

const DashboardAlerts: React.FC<DashboardAlertsProps> = ({ orders: propOrders }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<AtRiskOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AtRiskOrder | null>(null);
  const [cachedSelected, setCachedSelected] = useState<AtRiskOrder | null>(null);

  useEffect(() => { if (selected) setCachedSelected(selected); }, [selected]);

  useEffect(() => {
    if (propOrders) { setOrders(propOrders); setLoading(false); return; }
    apiService
      .getAtRiskOrders(20)
      .then((res: { data: { rows: AtRiskOrder[] } }) => setOrders(res.data.rows ?? []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [propOrders]);

  // ── Drawer field extraction (identical logic to ShipmentDetailsPopup) ──
  const renderDrawer = () => {
    const o = cachedSelected;
    if (!o) return null;

    const shipmentId  = fmt(o.shipment_id);
    const srcLoc      = fmt(o.source_location);
    const dstLoc      = fmt(o.destination_location ?? o.destination);
    const rawStatus   = (fmt(o.display_status ?? o.current_ui_group ?? o.status, "IN_PROGRESS"))
                          .toUpperCase().trim().replaceAll(/[\s-]+/g, "_");
    const statusStyle = STATUS_STYLE[rawStatus] ?? STATUS_STYLE["IN_PROGRESS"];
    const carrierName   = fmt(o.carrier_name);
    const mode          = fmtLabel(fmt(o.mode ?? o.transport_mode ?? o.primary_freight_mode, "Ocean"));
    const totalCost     = fmtCurrency(o.order_value_usd ?? o.total_cost);
    const productName   = fmt(o.product_name ?? o.product);
    const supplierName  = fmt(o.supplier_name);
    const region        = fmtLabel(fmt(o.region));
    const orderValue    = fmtCurrency(o.order_value_usd);
    const orderQty      = o.order_quantity == null ? "—" : String(o.order_quantity);
    const totalWeight   = o.total_weight_kg == null ? "—" : `${o.total_weight_kg} kg`;
    const customerId    = fmt(o.customer_id);
    const delayReason   = fmt(o.delay_reason);
    // If risk_flag just echoes the status (e.g. "At Risk", "AT_RISK"), treat it as empty
    const rawRiskFlag   = fmt(o.risk_flag);
    const STATUS_WORDS  = /^(at[\s_-]?risk|delayed|in[\s_-]?progress|pending|completed)$/i;
    const riskFlag      = STATUS_WORDS.test(rawRiskFlag) ? "—" : rawRiskFlag;
    // Best available reason: specific risk flag → delay reason → nothing
    const displayReason = riskFlag === "—" ? delayReason : riskFlag;

    const lastUpdated = (() => {
      if (!o.last_event_ts) return null;
      const d = new Date(String(o.last_event_ts));
      return Number.isNaN(d.getTime()) ? String(o.last_event_ts)
        : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    })();

    const PROGRESS_FALLBACK: Record<string, number> = {
      COMPLETED: 100, IN_PROGRESS: 55, AT_RISK: 35, DELAYED: 35, BLOCKED: 15, NOT_STARTED: 0, PENDING: 0,
    };
    const progressPct = o.progress_pct == null
      ? (PROGRESS_FALLBACK[rawStatus] ?? 0)
      : Math.min(100, Math.max(0, Number(o.progress_pct)));
    const stepsDone = Math.round((progressPct / 100) * JOURNEY_STEPS.length);

    const detailFields = [
      { label: "Carrier",         value: carrierName  },
      { label: "Order Value",     value: orderValue   },
      { label: "Order Quantity",  value: orderQty     },
      { label: "Total Weight",    value: totalWeight  },
      { label: "Source Location", value: srcLoc       },
      { label: "Destination",     value: dstLoc       },
      { label: "Customer ID",     value: customerId   },
      ...(region === "—" ? [] : [{ label: "Region", value: region }]),
    ];

    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: FONT }}>

        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px 24px", borderBottom: `1px solid ${BORDER_COLOR}`,
          background: "#fff", flexShrink: 0,
        }}>
          <span style={{ fontSize: "1.25rem", fontWeight: 700, color: TEXT_PRIMARY, fontFamily: FONT }}>
            Order Intelligence Panel
          </span>
          <button
            type="button"
            onClick={() => setSelected(null)}
            aria-label="Close panel"
            style={{
              background: "none", border: "none", cursor: "pointer", padding: 6,
              color: TEXT_MUTED, display: "flex", alignItems: "center",
              borderRadius: 6, lineHeight: 1,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 24px 8px" }}>

          {/* ID + status badge */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
            <h2 style={{ margin: 0, fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "0", fontFamily: FONT, lineHeight: 1.3 }}>
              {shipmentId}
            </h2>
            <span style={{
              flexShrink: 0, marginTop: 2, padding: "4px 14px", borderRadius: 20,
              fontSize: "0.8125rem", fontWeight: 600, fontFamily: FONT,
              background: statusStyle.bg, color: statusStyle.color,
              border: `1px solid ${statusStyle.border}`, whiteSpace: "nowrap",
            }}>
              {statusStyle.label}
            </span>
          </div>

          {/* Route subtitle */}
          <p style={{ margin: "0 0 2px", fontSize: "0.8125rem", color: TEXT_MUTED, fontFamily: FONT }}>
            {srcLoc} → {dstLoc}
          </p>
          {lastUpdated
            ? <p style={{ margin: "0 0 20px", fontSize: "0.8125rem", color: TEXT_MUTED, fontFamily: FONT }}>Updated {lastUpdated}</p>
            : <div style={{ marginBottom: 20 }} />
          }

          {/* Risk / delay reason banner — only shown when there's an actual reason */}
          {displayReason !== "—" && (
            <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 10, background: "#FFFBEB", border: "1px solid #FCD34D" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <AlertTriangle size={14} style={{ color: "#B45309", flexShrink: 0 }} />
                <span style={{ fontSize: "0.6875rem", fontWeight: 700, color: "#B45309", textTransform: "uppercase", letterSpacing: "0.05em", fontFamily: FONT }}>
                  {riskFlag === "—" ? "Delay Reason" : "Risk Reason"}
                </span>
              </div>
              <div style={{ fontSize: "0.875rem", color: "#92400E", fontWeight: 500, fontFamily: FONT }}>
                {displayReason}
              </div>
            </div>
          )}

          {/* Route info card */}
          <div style={{ background: BG_GREY, borderRadius: 12, padding: "16px 20px", marginBottom: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
              {[
                { label: "Product",        value: productName,   icon: null },
                { label: "Carrier",        value: carrierName,   icon: null },
                { label: "Transport Mode", value: mode,          icon: modeIcon(mode) },
                { label: "Supplier",       value: supplierName,  icon: null },
              ].map(({ label, value, icon }) => (
                <div key={label}>
                  <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 2, fontFamily: FONT }}>{label}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {icon}
                    <span style={{ fontSize: "0.8125rem", fontWeight: 400, color: TEXT_MUTED, fontFamily: FONT }}>{value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Details grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px 20px", marginBottom: 20 }}>
            {detailFields.map(({ label, value }) => (
              <div key={label}>
                <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 2, fontFamily: FONT }}>{label}</div>
                <div style={{ fontSize: "0.8125rem", fontWeight: 400, color: TEXT_MUTED, fontFamily: FONT }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Order Journey */}
          <h3 style={{ margin: "0 0 20px", fontSize: "1rem", fontWeight: 700, color: TEXT_PRIMARY, fontFamily: FONT }}>
            Order Journey
          </h3>
          <div style={{ marginBottom: 24 }}>
            {JOURNEY_STEPS.map(({ key, labelFn, sub, Icon }, idx) => {
              const done = idx < stepsDone;
              const last = idx === JOURNEY_STEPS.length - 1;
              return (
                <div key={key} style={{ display: "flex", gap: 14 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 40 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: "50%",
                      background: done ? GREEN : "#F1F5F9",
                      border: `2px solid ${done ? GREEN : "#CBD5E1"}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Icon size={18} style={{ color: done ? "#fff" : "#94A3B8" }} />
                    </div>
                    {!last && (
                      <div style={{ width: 2, flexGrow: 1, minHeight: 24, background: done ? GREEN : "#CBD5E1", margin: "2px 0" }} />
                    )}
                  </div>
                  <div style={{ paddingTop: 8, paddingBottom: last ? 0 : 20, flex: 1 }}>
                    <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 2, fontFamily: FONT }}>
                      {labelFn(srcLoc, dstLoc)}
                    </div>
                    <div style={{ fontSize: "0.8125rem", color: TEXT_MUTED, fontFamily: FONT }}>{sub}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cost Breakdown */}
          <div style={{ borderTop: `1px solid ${BORDER_COLOR}`, paddingTop: 20, marginBottom: 8 }}>
            <h3 style={{ margin: "0 0 16px", fontSize: "1rem", fontWeight: 700, color: TEXT_PRIMARY, fontFamily: FONT }}>
              Cost Breakdown
            </h3>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, fontFamily: FONT }}>Total Cost</span>
              <span style={{ fontSize: "0.8125rem", fontWeight: 400, color: TEXT_MUTED, fontFamily: FONT }}>{totalCost}</span>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px 28px", flexShrink: 0, background: "#fff" }}>
          <button
            type="button"
            onClick={() => { setSelected(null); navigate("/route-optimization"); }}
            style={{
              width: "100%", height: 52, borderRadius: 12,
              background: WL_RED, border: "none", color: "#fff",
              fontSize: "1rem", fontWeight: 700, cursor: "pointer",
              fontFamily: FONT, letterSpacing: "0.01em",
            }}
          >
            Plan Optimal Route
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* ── Card list ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderRadius: "14px",
          border: "1px solid #E5E7EB",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <Box>
            <Typography sx={{ fontFamily: FONT_CARD, fontWeight: 600, fontSize: "1rem", color: "#111111" }}>
              Alerts
            </Typography>
            <Typography sx={{ fontFamily: FONT_CARD, fontSize: "0.75rem", color: "#9CA3AF", mt: 0.25 }}>
              Live · requires immediate attention
            </Typography>
          </Box>
          <Box sx={{ bgcolor: WL_RED, color: "#FFFFFF", minWidth: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px", fontFamily: FONT_CARD, fontSize: "0.8125rem", fontWeight: 600 }}>
            {loading ? "…" : orders.length}
          </Box>
        </Box>

        {/* List */}
        <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
              <CircularProgress size={28} sx={{ color: WL_RED }} />
            </Box>
          )}
          {!loading && orders.length === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 32, color: "#D1D5DB" }} />
              <Typography sx={{ fontFamily: FONT_CARD, fontSize: "0.875rem", color: "#9CA3AF" }}>No at-risk orders found</Typography>
            </Box>
          )}
          {!loading && orders.length > 0 && orders.slice(0, 3).map((order, idx) => {
            const id        = fmt(order.shipment_id, `Order ${idx + 1}`);
            const region    = fmtLabel(fmt(order.region));
            const carrier   = fmt(order.carrier_name);
            const priority  = fmtLabel(fmt(order.priority_label));
            const value     = fmtMoney(order.order_value_usd);
            const rawFlag   = fmt(order.risk_flag);
            const SW_CARD   = /^(at[\s_-]?risk|delayed|in[\s_-]?progress|pending|completed)$/i;
            const riskFlag  = SW_CARD.test(rawFlag) ? "—" : rawFlag;
            const mode      = fmtLabel(fmt(order.primary_freight_mode));
            const pColor    = priorityColor(order.priority_label);
            const srcLoc    = fmt(order.source_location);
            const dstLoc    = fmt(order.destination_location ?? order.destination);
            const product   = fmt(order.product_name ?? order.product);
            const rawStatus = (fmt(order.display_status ?? order.current_ui_group ?? order.status, "AT_RISK"))
                                .toUpperCase().trim().replaceAll(/[\s-]+/g, "_");
            const isDelayed = rawStatus === "DELAYED";
            const borderClr = isDelayed ? WL_RED : "#EA580C";
            const hoverBg   = isDelayed ? "#FFF5F5" : "#FFF7ED";
            const statusSt  = STATUS_STYLE[rawStatus] ?? STATUS_STYLE["AT_RISK"];
            const delayRsn  = fmt(order.delay_reason);
            const resolvedReason = riskFlag === "—" ? delayRsn : riskFlag;
            const reasonTxt = fmtLabel(resolvedReason);

            return (
              <Box
                key={id + idx}
                onClick={() => setSelected(order)}
                sx={{
                  px: 2.5, py: 2,
                  borderBottom: "1px solid #F3F4F6",
                  cursor: "pointer",
                  borderLeft: `3px solid ${borderClr}`,
                  transition: "background 0.15s",
                  "&:hover": { bgcolor: hoverBg },
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                {/* Row 1: ID + status badge + value */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: FONT_CARD, fontSize: "0.875rem", fontWeight: 700, color: "#111111", whiteSpace: "nowrap" }}>{id}</Typography>
                    <span style={{
                      flexShrink: 0, padding: "2px 8px", borderRadius: 20,
                      fontSize: "0.6875rem", fontWeight: 700, fontFamily: FONT_CARD,
                      background: statusSt.bg, color: statusSt.color,
                      border: `1px solid ${statusSt.border}`, whiteSpace: "nowrap",
                    }}>{statusSt.label}</span>
                  </Box>
                  <Typography sx={{ fontFamily: FONT_CARD, fontSize: "0.875rem", fontWeight: 700, color: borderClr, flexShrink: 0, ml: 1 }}>{value}</Typography>
                </Box>

                {/* Row 2: Route */}
                {(srcLoc !== "—" || dstLoc !== "—") && (
                  <Typography sx={{ fontFamily: FONT_CARD, fontSize: "0.75rem", color: "#6B7280", mb: 0.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {srcLoc !== "—" ? srcLoc : "?"} → {dstLoc !== "—" ? dstLoc : "?"}
                  </Typography>
                )}

                {/* Row 3: Product */}
                {product !== "—" && (
                  <Typography sx={{ fontFamily: FONT_CARD, fontSize: "0.75rem", color: "#374151", fontWeight: 500, mb: 0.75, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {product}
                  </Typography>
                )}

                {/* Row 4: Reason chip (if available) */}
                {reasonTxt !== "—" && (
                  <Box sx={{ mb: 0.75 }}>
                    <Chip
                      label={reasonTxt}
                      size="small"
                      sx={{ fontFamily: FONT_CARD, fontSize: "0.6875rem", fontWeight: 600,
                        bgcolor: isDelayed ? "#FEF2F2" : "#FFF7ED",
                        color: isDelayed ? "#DC2626" : "#C2410C",
                        height: 20, borderRadius: "4px", maxWidth: "100%",
                        "& .MuiChip-label": { px: 1, overflow: "hidden", textOverflow: "ellipsis" } }}
                    />
                  </Box>
                )}

                {/* Row 5: Meta — region, carrier, mode, priority */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                  {region !== "—" && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PublicIcon sx={{ fontSize: 12, color: "#9CA3AF" }} />
                      <Typography sx={{ fontFamily: FONT_CARD, fontSize: "0.75rem", color: "#6B7280" }}>{region}</Typography>
                    </Box>
                  )}
                  {carrier !== "—" && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <LocalShippingOutlinedIcon sx={{ fontSize: 13, color: "#9CA3AF" }} />
                      <Typography sx={{ fontFamily: FONT_CARD, fontSize: "0.75rem", color: "#6B7280" }}>{carrier}</Typography>
                    </Box>
                  )}
                  {mode !== "—" && <Typography sx={{ fontFamily: FONT_CARD, fontSize: "0.75rem", color: "#9CA3AF" }}>{mode}</Typography>}
                  {priority !== "—" && <Typography sx={{ fontFamily: FONT_CARD, fontSize: "0.6875rem", fontWeight: 600, color: pColor }}>{priority}</Typography>}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        {!loading && orders.length > 0 && (
          <Box sx={{ px: 3, py: 2, borderTop: "1px solid #F3F4F6", flexShrink: 0 }}>
            <Button fullWidth onClick={() => navigate("/orders?status=AT_RISK")} sx={{ fontFamily: FONT_CARD, fontSize: "0.8125rem", fontWeight: 600, color: WL_RED, textTransform: "none", py: 0.75, borderRadius: "8px", "&:hover": { bgcolor: "#FEF2F2" } }}>
              View All →
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Detail panel — identical to ShipmentDetailsPopup ────────────── */}
      <>
        {/* Backdrop */}
        <div
          aria-hidden="true"
          onClick={() => setSelected(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.28)",
            zIndex: 1299,
            opacity: selected ? 1 : 0,
            transition: "opacity 0.22s ease",
            pointerEvents: selected ? "auto" : "none",
          }}
        />
        {/* Slide-in panel */}
        <div
          style={{
            position: "fixed", right: 0, top: 0, bottom: 0, width: 480, maxWidth: "100vw",
            background: "#fff", boxShadow: "-4px 0 40px rgba(0,0,0,0.12)",
            zIndex: 1300, display: "flex", flexDirection: "column", fontFamily: FONT,
            transform: selected ? "translateX(0)" : "translateX(100%)",
            transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {renderDrawer()}
        </div>
      </>
    </>
  );
};

export default DashboardAlerts;
